import React, { useEffect, useState } from "react";
import MainTimer from "./MainconTimer";
import { useUser } from "../../../../hooks/use-user";
import Info from "../../../../Images/ph_info-duotone.png";
import axios from "../../../../axios";
import ContestButton from "./ContestButton";
import ContestHistory from "./ContestHistory";
import {
  Trophy,
  Clock,
  Users,
  Coins,
  Wallet,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";

function Maincontest() {
  const { data: user } = useUser();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleTooltip, setVisibleTooltip] = useState(null);
  const [activeTab, setActiveTab] = useState("live");
  const [clickedContests, setClickedContests] = useState([]);

  // Modal state
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalState, setModalState] = useState("confirm"); // "confirm" | "insufficient" | "success" | "error"
  const [selectedContest, setSelectedContest] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await axios.get("getAllContestForUser");
        if (response?.data) {
          const sortedData = response?.data?.result.sort(
            (a, b) => new Date(b.createdOn) - new Date(a.createdOn),
          );
          setData(sortedData);
        }
      } catch (error) {
        console.error("Error fetching contests:", error);
      }
    }
    fetchData();
  }, []);

  const showTooltip = (id) => {
    setVisibleTooltip(id);
  };

  const hideTooltip = () => {
    setVisibleTooltip(null);
  };

  const handleContestPayment = async (id, entryFee, contestName) => {
    if (!user) {
      setErrorMessage("Please login to join contest");
      setModalState("error");
      onOpen();
      return;
    }

    const rechargeBalance = user?.unifiedWallet?.rechargedBalance || 0;
    const contestBalance = user?.unifiedWallet?.contestBalance || 0;
    const referralBalance = user?.unifiedWallet?.referralBalance || 0;
    const lockedBalance = user?.unifiedWallet?.lockedBalance || 0;
    const totalAvailable = rechargeBalance + contestBalance + referralBalance + lockedBalance;

    setSelectedContest({
      id,
      entryFee,
      contestName,
      rechargeBalance,
      contestBalance,
      referralBalance,
      lockedBalance,
      totalAvailable,
    });

    if (totalAvailable < entryFee) {
      setModalState("insufficient");
    } else {
      setModalState("confirm");
    }
    onOpen();
  };

  useEffect(() => {
    const saved = localStorage.getItem("activeContests");
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setClickedContests(parsed);
        }
      } catch {
        // Legacy single-value migration
        if (saved) {
          setClickedContests([saved]);
          localStorage.setItem("activeContests", JSON.stringify([saved]));
          localStorage.removeItem("activeContest");
        }
      }
    }
  }, []);

  const handleSetContest = (id) => {
    setClickedContests((prev) => {
      const updated = prev.includes(id) ? prev : [...prev, id];
      localStorage.setItem("activeContests", JSON.stringify(updated));
      return updated;
    });
  };

  const confirmJoinContest = async () => {
    if (!selectedContest) return;
    setLoading(true);
    setModalState("confirm");

    try {
      const payload = { contestId: selectedContest.id };
      const response = await axios.post("joinContestWithWallet", payload);

      if (response?.data?.success) {
        setModalState("success");
        // Remove from clicked contests so "Start Now" becomes active after reload
        setClickedContests((prev) => {
          const updated = prev.filter((id) => id !== selectedContest.id);
          localStorage.setItem("activeContests", JSON.stringify(updated));
          return updated;
        });
        setTimeout(() => {
          onClose();
          window.location.reload();
        }, 2000);
      } else {
        setErrorMessage(response?.data?.message || "Failed to join contest");
        setModalState("error");
      }
    } catch (error) {
      console.error("Error joining contest:", error);
      setErrorMessage(
        error?.response?.data?.message || "Error joining contest",
      );
      setModalState("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#2A1B3D] text-white font-sans selection:bg-purple-500 selection:text-white pb-20 pt-10">
      {/* Header / Tabs Section */}
      <div className="pt-8 px-4  max-w-6xl mx-auto">
        <div className="flex justify-center mb-8">
          <div className="bg-[#442C5C] p-1 rounded-full flex gap-1 shadow-lg">
            <button
              onClick={() => setActiveTab("live")}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${activeTab === "live"
                ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md transform scale-105"
                : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
            >
              <Trophy size={16} /> Live Contests
              {activeTab === "live" && (
                <span className="ml-1 bg-white text-orange-600 text-[10px] px-1.5 py-0.5 rounded-full animate-pulse">
                  ●
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("history")}
              className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all duration-300 flex items-center gap-2 ${activeTab === "history"
                ? "bg-gradient-to-r from-orange-500 to-pink-500 text-white shadow-md transform scale-105"
                : "text-gray-300 hover:text-white hover:bg-white/10"
                }`}
            >
              <Clock size={16} /> History
            </button>
          </div>
        </div>

        {/* Content Section */}
        {activeTab === "live" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in-up">
            {data &&
              data.map((e) => (
                <div
                  key={e._id}
                  className="bg-[#3F234E] rounded-3xl p-5 relative overflow-hidden shadow-xl border border-white/5 hover:border-purple-500/50 transition-all duration-300 group hover:-translate-y-1"
                >
                  {/* Card Glow Effect */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-purple-500/10 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>

                  <div className="relative z-10">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white mb-1 group-hover:text-purple-300 transition-colors">
                          {e.contestName}
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <span className="bg-white/10 px-2 py-0.5 rounded text-gray-300">
                            Entry: {e.entryCoin} coins
                          </span>
                          <span className="text-gray-500">•</span>
                          <span className="text-orange-400 flex items-center gap-1">
                            <Users size={12} /> {e.winners} Winners
                          </span>
                        </div>
                      </div>
                      <div
                        className="cursor-pointer text-gray-400 hover:text-white transition-colors"
                        onMouseEnter={() => showTooltip(e._id)}
                        onMouseLeave={hideTooltip}
                      >
                        <img
                          src={Info}
                          alt="Info"
                          className="w-5 h-5 opacity-70 hover:opacity-100"
                        />
                      </div>
                    </div>

                    {/* Prize Pool & Timer */}
                    <div className="bg-[#2A1B3D]/80 rounded-2xl p-4 mb-4 backdrop-blur-sm border border-white/5">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-gray-400 text-xs font-medium uppercase tracking-wider">
                          Prize Pool
                        </span>
                        <span className="text-xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center gap-1">
                          <Coins size={20} className="text-yellow-400" />
                          {e.prizePoolCoin} coins
                        </span>
                      </div>

                      {/* Timer Component Integration */}
                      <div className="flex justify-center bg-[#1F1229] rounded-xl py-2">
                        <MainTimer
                          contestId={e._id}
                          startTimeON={e?.startTime}
                          endTimeON={e?.endTime}
                        />
                      </div>
                    </div>

                    {/* Progress Bar (Mock visual for now or use real data if available in 'e') */}
                    {/* 
                     <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                            <span>150 Spots Left</span>
                            <span>1000 Total</span>
                        </div>
                         <div className="h-1.5 w-full bg-gray-700 rounded-full overflow-hidden">
                             <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 w-[70%]"><¸/div>
                         </div>
                     </div> 
                     */}

                    {/* Action Button */}
                    <ContestButton
                      startTimeON={e?.startTime}
                      endTimeON={e?.endTime}
                      contestId={e._id}
                      isContestSubmitted={e?.isContestSubmitted || false}
                      handleContestPayments={() => {
                        handleSetContest(e._id);
                        handleContestPayment(e._id, e.entryCoin, e.contestName);
                      }}
                    />

                    {/* Tooltip / Prize Breakdown Overlay */}
                    {visibleTooltip === e._id && (
                      <div
                        className="absolute inset-0 bg-[#2A1B3D]/95 backdrop-blur-md z-20 rounded-3xl p-5 flex flex-col animate-fade-in"
                        onMouseLeave={hideTooltip}
                      >
                        <div className="flex justify-between items-center mb-4 border-b border-white/10 pb-2">
                          <h4 className="font-bold text-white">
                            Prize Breakdown
                          </h4>
                          <button
                            onClick={hideTooltip}
                            className="text-gray-400 hover:text-white"
                          >
                            &times;
                          </button>
                        </div>

                        <div className="flex-1 overflow-y-auto space-y-2 pr-1 custom-scrollbar">
                          <div className="flex justify-between text-xs text-gray-500 px-2">
                            <span>RANK</span>
                            <span>WINNINGS</span>
                          </div>
                          {e.winnerPrizes?.map((prize, idx) => (
                            <div
                              key={idx}
                              className="flex justify-between items-center bg-white/5 p-2 rounded-lg text-sm"
                            >
                              <div className="font-bold text-white">
                                #{idx + 1}
                              </div>
                              <div className="font-bold text-green-400">
                                {prize} coins
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
          </div>
        )}

        {activeTab === "history" && (
          <div className="animate-fade-in">
            <ContestHistory />
          </div>
        )}
      </div>

      {/* Join Contest Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="md" isCentered>
        <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.700" />
        <ModalContent bg="transparent" boxShadow="none" maxW="420px" mx={4}>
          <ModalBody p={0}>
            {/* ── Confirm Join Modal ── */}
            {modalState === "confirm" && selectedContest && (
              <div className="bg-gradient-to-b from-[#3F234E] to-[#2A1B3D] rounded-3xl border border-white/10 overflow-hidden">
                {/* Header with glow */}
                <div className="relative px-6 pt-6 pb-4">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-purple-500/20 rounded-full blur-3xl"></div>
                  <div className="relative flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles size={18} className="text-yellow-400" />
                        <span className="text-xs font-semibold text-purple-300 uppercase tracking-wider">
                          Join Contest
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-white">
                        {selectedContest.contestName}
                      </h2>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 transition text-gray-400 hover:text-white"
                    >
                      <XCircle size={20} />
                    </button>
                  </div>
                </div>

                {/* Entry Fee Highlight */}
                <div className="mx-6 mb-4 bg-gradient-to-r from-purple-600/30 to-pink-600/30 rounded-2xl p-4 border border-purple-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300 text-sm">Entry Fee</span>
                    <span className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400 flex items-center gap-1">
                      <Coins size={22} className="text-yellow-400" />
                      {selectedContest.entryFee} coins
                    </span>
                  </div>
                </div>

                {/* Wallet Balance Breakdown */}
                <div className="mx-6 mb-4 space-y-2">
                  <div className="flex items-center gap-2 mb-3">
                    <Wallet size={16} className="text-purple-400" />
                    <span className="text-sm font-semibold text-gray-300">
                      Wallet Balance
                    </span>
                  </div>
                  <div className="bg-[#1F1229] rounded-xl p-3 space-y-2.5">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">
                        Recharge Balance
                      </span>
                      <span className="text-white font-semibold">
                        {selectedContest.rechargeBalance} coins
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">
                        Contest Winnings
                      </span>
                      <span className="text-white font-semibold">
                        {selectedContest.contestBalance} coins
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">
                        Referral Balance
                      </span>
                      <span className="text-white font-semibold">
                        {selectedContest.referralBalance} coins
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-400 text-sm">
                        Locked Balance
                      </span>
                      <span className="text-white font-semibold">
                        {selectedContest.lockedBalance} coins
                      </span>
                    </div>
                    <div className="border-t border-white/10 pt-2 flex justify-between items-center">
                      <span className="text-gray-300 text-sm font-medium">
                        Total Available
                      </span>
                      <span className="text-green-400 font-bold text-lg">
                        {selectedContest.totalAvailable} coins
                      </span>
                    </div>
                  </div>
                </div>

                {/* Deduction Breakdown */}
                <div className="mx-6 mb-6 bg-[#1F1229] rounded-xl p-3 space-y-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Deduction Breakdown
                  </span>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">From Recharge</span>
                    <span className="text-orange-400 font-medium">
                      -{" "}
                      {Math.min(
                        selectedContest.rechargeBalance,
                        selectedContest.entryFee,
                      )}{" "}
                      coins
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">From Winnings</span>
                    <span className="text-orange-400 font-medium">
                      -{" "}
                      {Math.max(
                        0,
                        Math.min(
                          selectedContest.contestBalance,
                          selectedContest.entryFee - Math.min(selectedContest.rechargeBalance, selectedContest.entryFee)
                        ),
                      )}{" "}
                      coins
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">From Referral</span>
                    <span className="text-orange-400 font-medium">
                      -{" "}
                      {Math.max(
                        0,
                        Math.min(
                          selectedContest.referralBalance,
                          selectedContest.entryFee - Math.min(selectedContest.rechargeBalance, selectedContest.entryFee) - Math.min(selectedContest.contestBalance, Math.max(0, selectedContest.entryFee - selectedContest.rechargeBalance))
                        ),
                      )}{" "}
                      coins
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">From Locked</span>
                    <span className="text-orange-400 font-medium">
                      -{" "}
                      {Math.max(
                        0,
                        selectedContest.entryFee -
                        Math.min(selectedContest.rechargeBalance, selectedContest.entryFee) -
                        Math.min(selectedContest.contestBalance, Math.max(0, selectedContest.entryFee - selectedContest.rechargeBalance)) -
                        Math.min(selectedContest.referralBalance, Math.max(0, selectedContest.entryFee - selectedContest.rechargeBalance - selectedContest.contestBalance)),
                      )}{" "}
                      coins
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="px-6 pb-6 flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 py-3 rounded-xl bg-white/5 text-gray-300 font-semibold hover:bg-white/10 transition border border-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmJoinContest}
                    disabled={loading}
                    className="flex-[1.5] py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 transform transition hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    ) : (
                      <>
                        Confirm & Join <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* ── Insufficient Balance Modal ── */}
            {modalState === "insufficient" && selectedContest && (
              <div className="bg-gradient-to-b from-[#3F234E] to-[#2A1B3D] rounded-3xl border border-white/10 overflow-hidden">
                {/* Header */}
                <div className="relative px-6 pt-6 pb-4">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-20 bg-orange-500/15 rounded-full blur-3xl"></div>
                  <div className="relative flex justify-between items-start">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center">
                        <AlertTriangle size={24} className="text-orange-400" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-white">
                          Insufficient Balance
                        </h2>
                        <p className="text-gray-400 text-xs">
                          You need more coins to join
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full bg-white/5 hover:bg-white/10 transition text-gray-400 hover:text-white"
                    >
                      <XCircle size={20} />
                    </button>
                  </div>
                </div>

                {/* Balance Info */}
                <div className="mx-6 mb-4 bg-[#1F1229] rounded-xl p-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Entry Fee</span>
                    <span className="text-white font-bold">
                      {selectedContest.entryFee} coins
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Your Balance</span>
                    <span className="text-red-400 font-bold">
                      {selectedContest.totalAvailable} coins
                    </span>
                  </div>
                  <div className="border-t border-white/10 pt-2 flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Shortfall</span>
                    <span className="text-orange-400 font-bold">
                      {selectedContest.entryFee -
                        selectedContest.totalAvailable}{" "}
                      coins
                    </span>
                  </div>
                </div>

                {/* Conversion hint */}
                <div className="mx-6 mb-4 flex items-center gap-2 bg-purple-500/10 rounded-xl px-4 py-3 border border-purple-500/20">
                  <Coins size={16} className="text-yellow-400 shrink-0" />
                  <p className="text-xs text-gray-300">
                    <span className="text-purple-300 font-semibold">Tip:</span>{" "}
                    You need{" "}
                    {selectedContest.entryFee - selectedContest.totalAvailable}{" "}
                    more coins. Recharge ₹
                    {Math.ceil(
                      (selectedContest.entryFee -
                        selectedContest.totalAvailable) /
                      100,
                    )}{" "}
                    to get them (1₹ = 100 coins)
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="px-6 pb-6 flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 py-3 rounded-xl bg-white/5 text-gray-300 font-semibold hover:bg-white/10 transition border border-white/10"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      navigate("/dash/wallet");
                    }}
                    className="flex-[1.5] py-3 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transform transition hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                  >
                    <Wallet size={18} /> Recharge Now
                  </button>
                </div>
              </div>
            )}

            {/* ── Success Modal ── */}
            {modalState === "success" && (
              <div className="bg-gradient-to-b from-[#3F234E] to-[#2A1B3D] rounded-3xl border border-white/10 overflow-hidden text-center py-10 px-6">
                <div className="relative">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-green-500/20 rounded-full blur-3xl"></div>
                  <div className="relative w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-green-400/20 to-emerald-500/20 border-2 border-green-500/30 flex items-center justify-center">
                    <CheckCircle size={40} className="text-green-400" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">
                  You're In!
                </h2>
                <p className="text-gray-400 text-sm mb-1">
                  Successfully joined the contest
                </p>
                <p className="text-purple-300 font-semibold">
                  {selectedContest?.contestName}
                </p>
                <div className="mt-4 inline-flex items-center gap-1.5 bg-green-500/10 text-green-400 text-xs font-medium px-3 py-1.5 rounded-full border border-green-500/20">
                  <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                  Redirecting...
                </div>
              </div>
            )}

            {/* ── Error Modal ── */}
            {modalState === "error" && (
              <div className="bg-gradient-to-b from-[#3F234E] to-[#2A1B3D] rounded-3xl border border-white/10 overflow-hidden text-center py-10 px-6">
                <div className="relative">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-red-500/15 rounded-full blur-3xl"></div>
                  <div className="relative w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-red-400/20 to-pink-500/20 border-2 border-red-500/30 flex items-center justify-center">
                    <XCircle size={40} className="text-red-400" />
                  </div>
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Oops!</h2>
                <p className="text-gray-400 text-sm mb-4">{errorMessage}</p>
                <button
                  onClick={onClose}
                  className="px-8 py-2.5 rounded-xl bg-white/10 text-white font-semibold hover:bg-white/15 transition border border-white/10"
                >
                  Got it
                </button>
              </div>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
}

export default Maincontest;

